import React, { useState, useEffect } from 'react';
import { 
  Menu, 
  X, 
  Home, 
  Stethoscope, 
  FileText, 
  LayoutDashboard, 
  Heart, 
  Users,
  LogIn,
  UserPlus,
  LogOut,
  User,
  Lock
} from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import '../styles/Navbar.css';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  // القائمة الأساسية للروابط (مشتركة لجميع المستخدمين)
  const baseNavItems = [
    { name: 'الرئيسية', path: '/', icon: Home },
    { name: 'الاستشارات', path: '/consultations', icon: Stethoscope },
    { name: 'المقالات', path: '/articles', icon: FileText },
    { name: 'من نحن', path: '/about', icon: Users },
  ];

  // إنشاء قائمة الروابط الديناميكية
  const getNavItems = () => {
    // البدء بالقائمة الأساسية
    let items = [...baseNavItems];
    
    // إضافة لوحة التحكم فقط إذا كان المستخدم مسؤولاً
    if (isAuthenticated && user?.is_admin) {
      items.push({ 
        name: 'لوحة التحكم', 
        path: '/dashboard', 
        icon: LayoutDashboard,
        isAdmin: true 
      });
    }
    
    return items;
  };

  const navItems = getNavItems();

  useEffect(() => {
    // التحقق من حالة المصادقة من localStorage
    const checkAuth = () => {
      try {
        const userData = localStorage.getItem('user');
        if (userData) {
          const parsedUser = JSON.parse(userData);
          setIsAuthenticated(parsedUser.isAuthenticated === true);
          setUser(parsedUser);
        } else {
          setIsAuthenticated(false);
          setUser(null);
        }
      } catch (error) {
        console.error('Error parsing user data:', error);
        setIsAuthenticated(false);
        setUser(null);
      }
    };

    checkAuth();

    // الاستماع لتغييرات localStorage من نفس التبويب
    const handleStorageChange = (e) => {
      if (e.key === 'user' || e.key === 'access_token') {
        checkAuth();
      }
    };

    // الاستماع لتغييرات localStorage من التبويبات الأخرى
    window.addEventListener('storage', (e) => {
      if (e.key === 'user' || e.key === 'access_token') {
        checkAuth();
      }
    });

    // استمع لتغييرات localStorage من نفس التبويب
    window.addEventListener('storageChange', checkAuth);
    
    return () => {
      window.removeEventListener('storage', checkAuth);
      window.removeEventListener('storageChange', checkAuth);
    };
  }, []);

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      if (token) {
        // محاولة تسجيل الخروج من الخادم
        const response = await fetch('https://darkslateblue-eel-151368.hostingersite.com/api/auth/logout', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          console.warn('Server logout failed, proceeding with local logout');
        }
      }
    } catch (error) {
      console.error('Logout API error:', error);
    } finally {
      // مسح بيانات المستخدم من localStorage
      localStorage.removeItem('user');
      localStorage.removeItem('access_token');
      
      // إرسال حدث لتحديث المكونات الأخرى
      window.dispatchEvent(new Event('storageChange'));
      
      // إعادة تعيين حالة المصادقة
      setIsAuthenticated(false);
      setUser(null);
      
      // إغلاق القائمة المتنقلة
      setIsOpen(false);
      
      // إعادة تحميل الصفحة لتحديث الحالة
      window.location.reload();
      
      // إظهار رسالة نجاح
      alert('تم تسجيل الخروج بنجاح');
    }
  };

  const handleAuthAction = (path) => {
    setIsOpen(false);
    
    // إذا حاول الدخول للوحة التحكم وهو غير مسجل
    if (path === '/dashboard' && (!isAuthenticated || !user?.is_admin)) {
      alert('عذراً، الصفحة متاحة للمسؤولين فقط');
      return;
    }
    
    navigate(path);
  };

  return (
    <nav className="navbar" dir="rtl">
      <div className="navbar-container">
        <div className="navbar-content">
          {/* Logo */}
          <Link to="/" className="navbar-logo">
            <div className="logo-icon-container">
              <Heart className="logo-icon" />
            </div>
            <div className="logo-text-container">
              <h1 className="logo-title">العيادة الطبية</h1>
              <p className="logo-subtitle">صحتك تهمنا</p>
            </div>
          </Link>

          {/* Desktop Menu */}
          <div className="desktop-menu">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`nav-item ${isActive(item.path) ? 'active' : ''} ${item.isAdmin ? 'admin-item' : ''}`}
                  title={item.isAdmin ? 'لوحة التحكم (للمسؤولين فقط)' : ''}
                >
                  <Icon className="nav-item-icon" />
                  <span>{item.name}</span>
                  {item.isAdmin && <Lock className="admin-badge-icon" />}
                </Link>
              );
            })}

            {/* Authentication Buttons - Desktop */}
            <div className="auth-buttons">
              {isAuthenticated ? (
                <>
                 
                  
                  {/* Logout Button */}
                  <button 
                    onClick={handleLogout}
                    className="nav-button logout-button"
                  >
                    <LogOut className="button-icon" />
                    <span>تسجيل خروج</span>
                  </button>
                </>
              ) : (
                <>
                  {/* Login Button */}
                  <button 
                    onClick={() => handleAuthAction('/auth')}
                    className="nav-button login-button"
                  >
                    <LogIn className="button-icon" />
                    <span>تسجيل دخول</span>
                  </button>
                  
                
                </>
              )}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="mobile-menu-button"
          >
            {isOpen ? <X className="mobile-menu-icon" /> : <Menu className="mobile-menu-icon" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="mobile-menu">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsOpen(false)}
                  className={`mobile-nav-item ${isActive(item.path) ? 'active' : ''} ${item.isAdmin ? 'mobile-admin-item' : ''}`}
                  title={item.isAdmin ? 'لوحة التحكم (للمسؤولين فقط)' : ''}
                >
                  <Icon className="mobile-nav-item-icon" />
                  <span>{item.name}</span>
                  {item.isAdmin && <Lock className="mobile-admin-badge-icon" />}
                </Link>
              );
            })}

            {/* Authentication Buttons - Mobile */}
            <div className="mobile-auth-buttons">
              {isAuthenticated ? (
                <>
                  {/* User Info - Mobile */}
                  <div className="mobile-user-info">
                    <User className="mobile-user-icon" />
                    <div className="mobile-user-details">
                      <span className="mobile-user-name">{user?.email || 'مستخدم'}</span>
                      {user?.is_admin ? (
                        <span className="mobile-user-badge admin">مسؤول</span>
                      ) : (
                        <span className="mobile-user-badge user">مستخدم</span>
                      )}
                    </div>
                  </div>
                  
                  {/* Logout Button - Mobile */}
                  <button 
                    onClick={() => {
                      setIsOpen(false);
                      handleLogout();
                    }}
                    className="mobile-nav-button mobile-logout-button"
                  >
                    <LogOut className="mobile-button-icon" />
                    <span>تسجيل خروج</span>
                  </button>
                </>
              ) : (
                <>
                  {/* Login Button - Mobile */}
                  <button 
                    onClick={() => handleAuthAction('/auth')}
                    className="mobile-nav-button mobile-login-button"
                  >
                    <LogIn className="mobile-button-icon" />
                    <span>تسجيل دخول</span>
                  </button>
                  
                 
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;